import { ElementRef, InjectionToken, Injector, ViewContainerRef } from '@angular/core';
import { app } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { ICMS_CLIENT_CONFIG, NICmsClientConfig } from '@icms/client/config/icms-client.config';
import { NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import { IcmsIcons } from '@icms/ui/icons/generated/icms-icons';
import { BehaviorSubject, Subject } from 'rxjs';
import { IValidtionEvent } from '../component/icms-content-component-validation.service';
import { NICmsContentField } from './icms-content-field.interfaces';

const spacingsFn = () => {
    const spacings = app.injector.get<NICmsClientConfig.IConfig>(ICMS_CLIENT_CONFIG).spacings;
    const sizeId = app.injector.get<Client>(Client).sizeId;

    if (typeof spacings === 'function') {
        return spacings(sizeId);
    }

    return spacings;
};

declare module '@df/catalog/catalog.interfaces' {
    namespace Catalog {
        interface IStylesValues {
            hidden?: boolean;
            sectionId?: string;
        }
    }
}

export namespace NICmsContentComponent {
    /**
     * This injection token is needed for icms content to access outlet instance
     * magic needed cause of circular dependencies
     */
    export const OutletInstance = new InjectionToken<any>('OUTLET_INSTANCE');

    export interface IEditableComponent {
        injector: Injector;
        node?: HTMLElement | ElementRef<HTMLElement>;
        nodes$?: BehaviorSubject<HTMLElement[]>;
        viewContainerRef?: ViewContainerRef;
        variant?: string | TVariant;
        _setICmsContent?: (content: IData) => void;
        _icmsContent$?: BehaviorSubject<IData | undefined>;
        _iCmsMeta?: IMeta;
    }

    export type TVariant = { id: string; label: string };

    export interface IComponent extends IEditableComponent {
        /**
         * If false detection tieggers are off
         */
        initiated?: boolean;

        outletOptions?: IOutletOptions;

        /**
         * Set from cms during ngOnInit stage
         */
        iCmsValidation$?: Subject<IValidtionEvent | undefined | void>;

        /**
         * Public method to trigger change detection from outside
         */
        detectChanges: (changes?: IDetectionChanges, immediate?: boolean) => void;
    }

    export interface IDetectionChanges {
        [key: string]: Change | Mutation;
    }

    export class Change<T = any> {
        constructor(
            public currentValue: T,
            public previousValue: T
        ) {}
    }

    export class Mutation {
        constructor(public changeEvent: NICmsDataChange.IChangeDataEvent) {}
    }

    export interface IOutletOptions {
        context?: any;
    }

    export interface IMeta<TComponent extends IComponent = IComponent, TData extends IDataWithStyles = IDataWithStyles> {
        /**
         * Component type (ts class of the angular component)
         */
        type: ICmsComponentType<TComponent>;
        /**
         * Component id required to create it
         */
        id: string;
        /**
         * Name used in iCMS
         */
        name: string;
        /**
         * mark as advanced if can be safely used by non-tco editors, that is only true for complete components like hero image
         */
        isAdvanced?: boolean;
        /**
         * that is only true for components that can't have preview like custom forms (if default data is not enough to display content)
         */
        hidePreview?: boolean;
        /**
         * Description used in iCMS
         */
        description?: string;
        /**
         * Tags used in iCMS
         */
        tags?: string[];
        /**
         * Only listed when selected outlet allows it
         */
        onDemandOnly?: boolean;

        /**
         * Disable removing unnecessary data form component
         */
        disbaleDataSanitization?: boolean;
        /**
         * Additional allowed keys that are not defined in fields
         */
        additionalAllowedKeys?: string[];

        /**
         * Sort order
         */
        priority?: number;

        /**
         * Image preview
         */
        image?: string;

        /**
         * Fields definitions
         */
        fields: NICmsContentField.IField[];
        initialData?: any;

        /**
         * Mark component as deprecated
         */
        isDeprecated?: boolean;
        /**
         * Optional deprecated replace message
         */
        replacementComponent?: string;
        /**
         * Optional deprecated date message
         */
        removeDate?: string;
        /**
         * Optional entry to auto-provide component data from a block
         */
        block?: {
            /**
             * Block URL to load from
             */
            url: string;
            /**
             * Block scope
             * used to identify what full-page-caches need to be updated when changed
             */
            scope?: 'website';
            /**
             * Rules for block autoloading
             */
            autoload?: 'prerender' | 'bootstrap';
            /**
             * Block available variants
             */
            variantOptions?: NICmsContentField.IOption<string>[] | (() => NICmsContentField.IOption<string>[] | undefined);
            /**
             * Resolvers
             */
            resolvers?: {
                [key: string]: (data: TData) => Promise<any>;
            };
        };
    }

    export interface IData {
        id: string;
    }

    export interface IDataWithStyles extends IData {
        styles: IComponentStyles;
    }

    export interface IComponentStyles {
        hidden?: boolean;
        sectionId?: string;
        bg?: string;
        width?: string;
        mt?: string;
        mb?: string;
        ml?: string;
        mr?: string;
        pt?: string;
        pb?: string;
        pl?: string;
        pr?: string;
    }

    export interface IContentMeta {
        type?: 'blockContent' | 'component' | 'componentOrBlockContent';
        // max depth for change detection trigger
        watchDepth?: number;
        test?: boolean;
    }

    export declare interface ICmsComponentType<T> extends Function {
        new (...args: any[]): T;
        _iCmsMeta?: IMeta;
        // _iCmsContent?: (IContentMeta & { property: string })[];
    }

    export enum EFieldGroup {
        content = 'Content',
        settings = 'Settings',
        styles = 'Styles'
    }

    export const fieldGroupIcons: { [key: string]: IcmsIcons } = {
        [EFieldGroup.content]: IcmsIcons.Content,
        [EFieldGroup.styles]: IcmsIcons.Style,
        [EFieldGroup.settings]: IcmsIcons.Settings
    };

    export const basicStyleFields: NICmsContentField.IField[] = [
        <NICmsContentField.IToggleField>{
            key: 'styles.hidden',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Hide',
            group: EFieldGroup.styles,
            default: false,
            labels: { true: 'Hidden', false: 'Visible' }
        },
        {
            key: 'styles.sectionId',
            type: NICmsContentField.EFieldType.text,
            title: 'HTML id',
            group: EFieldGroup.styles,
            description: 'HTML id attribute for section to use with anchor links'
        }
    ];

    export const styleFields: NICmsContentField.IField[] = [
        ...basicStyleFields,
        <NICmsContentField.IChoiceField>{
            key: 'styles.mt',
            type: NICmsContentField.EFieldType.choice,
            title: 'Top margin',
            group: EFieldGroup.styles,
            options: spacingsFn,
            default: undefined
        },
        <NICmsContentField.IChoiceField>{
            key: 'styles.mb',
            type: NICmsContentField.EFieldType.choice,
            title: 'Bottom margin',
            group: EFieldGroup.styles,
            options: spacingsFn,
            default: undefined
        },
        <NICmsContentField.IChoiceField>{
            key: 'styles.ml',
            type: NICmsContentField.EFieldType.choice,
            title: 'Left margin',
            group: EFieldGroup.styles,
            options: spacingsFn,
            default: undefined
        },
        <NICmsContentField.IChoiceField>{
            key: 'styles.mr',
            type: NICmsContentField.EFieldType.choice,
            title: 'Right margin',
            group: EFieldGroup.styles,
            options: spacingsFn,
            default: undefined
        },
        <NICmsContentField.IChoiceField>{
            key: 'styles.pt',
            type: NICmsContentField.EFieldType.choice,
            title: 'Top padding',
            group: EFieldGroup.styles,
            options: spacingsFn,
            default: undefined
        },
        <NICmsContentField.IChoiceField>{
            key: 'styles.pb',
            type: NICmsContentField.EFieldType.choice,
            title: 'Bottom padding',
            group: EFieldGroup.styles,
            options: spacingsFn,
            default: undefined
        },
        <NICmsContentField.IChoiceField>{
            key: 'styles.pl',
            type: NICmsContentField.EFieldType.choice,
            title: 'Left padding',
            group: EFieldGroup.styles,
            options: spacingsFn,
            default: undefined
        },
        <NICmsContentField.IChoiceField>{
            key: 'styles.pr',
            type: NICmsContentField.EFieldType.choice,
            title: 'Right padding',
            group: EFieldGroup.styles,
            options: spacingsFn,
            default: undefined
        }
    ];

    export const backgroundField = <NICmsContentField.IChoiceField>{
        key: 'styles.bg',
        type: NICmsContentField.EFieldType.choice,
        title: 'Background Color',
        group: NICmsContentComponent.EFieldGroup.styles,
        previewMode: 'color',
        options: () => app.injector.get<NICmsClientConfig.IConfig>(ICMS_CLIENT_CONFIG).colors,
        default: undefined
    };

    export const widthField = <NICmsContentField.IChoiceField>{
        key: 'styles.width',
        type: NICmsContentField.EFieldType.choice,
        title: 'Width',
        group: NICmsContentComponent.EFieldGroup.styles,
        default: 0,
        options: () => [
            {
                key: 0,
                title: 'Full width'
            },
            ...[12, 10, 8, 6, 4, 2].map(key => ({ key, title: `${key} columns` }))
        ]
    };
}
